import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
  LinearProgress,
} from "@mui/material";
import {
  DoneAllOutlined,
  LightbulbOutlined,
  KeyOutlined,
  RestartAltOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { tokens, radius, shadow } from "../../theme/theme";
import { plural } from "../utils/format";

const ALPHABET = "АБВГҐДЕЄЖЗИІЇЙКЛМНОПРСТУФХЦЧШЩЬЮЯ'";
const BLOCK = "#";

const ACROSS = "across";
const DOWN = "down";

const key = (r, c) => r + ":" + c;

/* ----------------------------------------------------------- puzzle helpers */

function buildIndex(puzzle) {
  const numbers = {};
  const entryByCell = { across: {}, down: {} };
  const entries = puzzle.entries || [];

  entries.forEach((entry) => {
    const startKey = key(entry.row, entry.col);
    if (numbers[startKey] === undefined || entry.num < numbers[startKey]) {
      numbers[startKey] = entry.num;
    }
    for (let i = 0; i < entry.answer.length; i++) {
      const r = entry.dir === ACROSS ? entry.row : entry.row + i;
      const c = entry.dir === ACROSS ? entry.col + i : entry.col;
      entryByCell[entry.dir][key(r, c)] = entry;
    }
  });

  return { numbers, entryByCell };
}

function isOpen(puzzle, r, c) {
  if (r < 0 || c < 0 || r >= puzzle.grid.length) return false;
  const row = puzzle.grid[r];
  if (!row || c >= row.length) return false;
  return row[c] !== BLOCK && row[c] !== " ";
}

function solutionAt(puzzle, r, c) {
  const row = puzzle.grid[r];
  return row ? row[c] : BLOCK;
}

function entryCells(entry) {
  const cells = [];
  for (let i = 0; i < entry.answer.length; i++) {
    cells.push({
      row: entry.dir === ACROSS ? entry.row : entry.row + i,
      col: entry.dir === ACROSS ? entry.col + i : entry.col,
    });
  }
  return cells;
}

/* ------------------------------------------------------------------ component */

export default function CrosswordGrid({
  puzzle,
  storageKey,
  onSolved,
  onRestoredSolved,
  solvedAlready,
}) {
  const { numbers, entryByCell } = useMemo(() => buildIndex(puzzle), [puzzle]);

  const acrossEntries = useMemo(
    () => puzzle.entries.filter((e) => e.dir === ACROSS).sort((a, b) => a.num - b.num),
    [puzzle]
  );
  const downEntries = useMemo(
    () => puzzle.entries.filter((e) => e.dir === DOWN).sort((a, b) => a.num - b.num),
    [puzzle]
  );

  const firstEntry = acrossEntries[0] || downEntries[0];

  const [letters, setLetters] = useState({});
  const [revealed, setRevealed] = useState({});
  const [wrong, setWrong] = useState({});
  const [cursor, setCursor] = useState(
    firstEntry ? { row: firstEntry.row, col: firstEntry.col } : { row: 0, col: 0 }
  );
  const [direction, setDirection] = useState(firstEntry ? firstEntry.dir : ACROSS);
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [complete, setComplete] = useState(false);
  const [pendingHint, setPendingHint] = useState(null);

  // `revealed` only paints the revealed cells and is wiped by "Очистити";
  // `hintsTotal` is the day's running tally and never goes down, so clearing
  // the grid cannot buy back eligibility.
  const [hintsTotal, setHintsTotal] = useState(0);

  const inputs = useRef({});
  const reportedRef = useRef(false);
  const hydratedRef = useRef(false);

  /** Any revealed letter costs eligibility for the day's win. */
  const hintsUsed = hintsTotal;
  const disqualified = hintsUsed > 0;

  /* ------------------------------------------------------------ completeness */

  const totalCells = useMemo(() => {
    let n = 0;
    for (let r = 0; r < puzzle.grid.length; r++) {
      for (let c = 0; c < puzzle.grid[r].length; c++) {
        if (isOpen(puzzle, r, c)) n++;
      }
    }
    return n;
  }, [puzzle]);

  const filledCells = useMemo(
    () => Object.keys(letters).filter((k) => letters[k]).length,
    [letters]
  );

  /** Is this set of letters a complete, correct solution? */
  const isSolvedWith = useCallback(
    (map) => {
      for (let r = 0; r < puzzle.grid.length; r++) {
        for (let c = 0; c < puzzle.grid[r].length; c++) {
          if (!isOpen(puzzle, r, c)) continue;
          if ((map[key(r, c)] || "") !== solutionAt(puzzle, r, c)) return false;
        }
      }
      return true;
    },
    [puzzle]
  );

  /* ------------------------------------------------------ persisted progress */

  useEffect(() => {
    if (!storageKey) {
      hydratedRef.current = true;
      return;
    }
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) || {};
        const savedLetters = parsed.letters || {};
        const savedRevealed = parsed.revealed || {};

        setLetters(savedLetters);
        setRevealed(savedRevealed);
        setHintsTotal(parsed.hintsTotal || Object.keys(savedRevealed).length);
        if (parsed.startedAt) setStartedAt(parsed.startedAt);

        // Already finished before this page load: restore it as done and treat
        // the result as reported. Without this the completeness effect below
        // fires on every reload and reopens the win dialog with a 0s time.
        if (isSolvedWith(savedLetters)) {
          setComplete(true);
          reportedRef.current = true;
          if (onRestoredSolved) {
            onRestoredSolved({
              hints: parsed.hintsTotal || Object.keys(savedRevealed).length,
            });
          }
        }
      }
    } catch (e) {
      /* private mode / disabled storage — progress just won't persist */
    }
    hydratedRef.current = true;
    // Runs once per puzzle; the callbacks are intentionally not dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    // Never write before hydration, or the empty initial state would wipe
    // whatever was stored a moment earlier.
    if (!storageKey || !hydratedRef.current) return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          letters: letters,
          revealed: revealed,
          hintsTotal: hintsTotal,
          startedAt: startedAt,
          completedAt: complete ? Date.now() : 0,
        })
      );
    } catch (e) {
      /* ignore quota / disabled storage */
    }
  }, [storageKey, letters, revealed, hintsTotal, startedAt, complete]);

  /* -------------------------------------------------------- solve detection */

  useEffect(() => {
    if (complete) return;
    if (filledCells < totalCells) return;
    if (!isSolvedWith(letters)) return;

    setComplete(true);
    if (!reportedRef.current) {
      reportedRef.current = true;
      if (onSolved) {
        onSolved({
          seconds: Math.round((Date.now() - startedAt) / 1000),
          hints: hintsUsed,
          eligible: hintsUsed === 0,
        });
      }
    }
  }, [
    letters,
    filledCells,
    totalCells,
    complete,
    isSolvedWith,
    onSolved,
    startedAt,
    hintsUsed,
  ]);

  /* -------------------------------------------------------------- navigation */

  const focusCell = useCallback((r, c) => {
    const el = inputs.current[key(r, c)];
    if (el) {
      el.focus();
      try {
        el.setSelectionRange(el.value.length, el.value.length);
      } catch (e) {
        /* number-ish inputs can refuse; harmless */
      }
    }
  }, []);

  const selectCell = useCallback(
    (r, c, dir) => {
      setCursor({ row: r, col: c });
      if (dir) setDirection(dir);
      focusCell(r, c);
    },
    [focusCell]
  );

  const currentEntry = entryByCell[direction][key(cursor.row, cursor.col)];

  const step = useCallback(
    (dr, dc) => {
      let r = cursor.row + dr;
      let c = cursor.col + dc;
      while (r >= 0 && c >= 0 && r < puzzle.grid.length && c < puzzle.cols) {
        if (isOpen(puzzle, r, c)) {
          selectCell(r, c, dr !== 0 ? DOWN : ACROSS);
          return true;
        }
        r += dr;
        c += dc;
      }
      return false;
    },
    [cursor, puzzle, selectCell]
  );

  /** Move one cell forward inside the current word (no jumping between words). */
  const advance = useCallback(
    (back) => {
      const dr = direction === DOWN ? (back ? -1 : 1) : 0;
      const dc = direction === ACROSS ? (back ? -1 : 1) : 0;
      const r = cursor.row + dr;
      const c = cursor.col + dc;
      if (isOpen(puzzle, r, c) && entryByCell[direction][key(r, c)] === currentEntry) {
        selectCell(r, c);
      }
    },
    [cursor, direction, puzzle, entryByCell, currentEntry, selectCell]
  );

  const goToEntry = useCallback(
    (entry) => {
      const cells = entryCells(entry);
      const empty = cells.filter((cell) => !letters[key(cell.row, cell.col)])[0];
      const target = empty || cells[0];
      selectCell(target.row, target.col, entry.dir);
    },
    [letters, selectCell]
  );

  const jumpEntry = useCallback(
    (delta) => {
      const list = direction === ACROSS ? acrossEntries : downEntries;
      const other = direction === ACROSS ? downEntries : acrossEntries;
      const index = list.indexOf(currentEntry);
      if (index === -1) {
        if (list[0]) goToEntry(list[0]);
        return;
      }
      const next = index + delta;
      if (next >= 0 && next < list.length) goToEntry(list[next]);
      else if (other.length) goToEntry(delta > 0 ? other[0] : other[other.length - 1]);
    },
    [direction, acrossEntries, downEntries, currentEntry, goToEntry]
  );

  /* ------------------------------------------------------------------ typing */

  const setLetter = useCallback((r, c, value) => {
    const k = key(r, c);
    setLetters((prev) => {
      const next = Object.assign({}, prev);
      if (value) next[k] = value;
      else delete next[k];
      return next;
    });
    setWrong((prev) => {
      if (!prev[k]) return prev;
      const next = Object.assign({}, prev);
      delete next[k];
      return next;
    });
  }, []);

  const handleChange = (r, c, rawValue) => {
    if (complete) return;
    const raw = (rawValue || "").toUpperCase();
    const ch = raw.slice(-1);
    if (!ch) {
      setLetter(r, c, "");
      return;
    }
    if (ALPHABET.indexOf(ch) === -1) return;
    setLetter(r, c, ch);
    setTimeout(() => advance(false), 0);
  };

  const handleKeyDown = (event, r, c) => {
    const k = key(r, c);

    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (direction !== ACROSS) setDirection(ACROSS);
      step(0, 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (direction !== ACROSS) setDirection(ACROSS);
      step(0, -1);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      if (direction !== DOWN) setDirection(DOWN);
      step(1, 0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (direction !== DOWN) setDirection(DOWN);
      step(-1, 0);
    } else if (event.key === "Backspace") {
      event.preventDefault();
      if (complete) return;
      if (letters[k]) setLetter(r, c, "");
      else {
        const dr = direction === DOWN ? -1 : 0;
        const dc = direction === ACROSS ? -1 : 0;
        const pr = r + dr;
        const pc = c + dc;
        if (isOpen(puzzle, pr, pc)) {
          setLetter(pr, pc, "");
          selectCell(pr, pc);
        }
      }
    } else if (event.key === "Delete") {
      event.preventDefault();
      setLetter(r, c, "");
    } else if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      setDirection(direction === ACROSS ? DOWN : ACROSS);
    } else if (event.key === "Tab") {
      event.preventDefault();
      jumpEntry(event.shiftKey ? -1 : 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      jumpEntry(1);
    }
  };

  const handleCellClick = (r, c) => {
    if (cursor.row === r && cursor.col === c) {
      const flipped = direction === ACROSS ? DOWN : ACROSS;
      if (entryByCell[flipped][key(r, c)]) setDirection(flipped);
    } else {
      const hasCurrent = !!entryByCell[direction][key(r, c)];
      selectCell(r, c, hasCurrent ? direction : direction === ACROSS ? DOWN : ACROSS);
    }
  };

  /* ------------------------------------------------------------------ actions */

  const checkAnswers = () => {
    const bad = {};
    Object.keys(letters).forEach((k) => {
      const parts = k.split(":");
      const r = Number(parts[0]);
      const c = Number(parts[1]);
      if (letters[k] && letters[k] !== solutionAt(puzzle, r, c)) bad[k] = true;
    });
    setWrong(bad);
  };

  /**
   * Hints are gated: the first one asks for confirmation, because it ends the
   * player's chance of being recorded as a winner for the day. Once they are
   * already disqualified there is nothing left to warn about, so further
   * hints apply straight away.
   */
  const requestHint = (kind) => {
    if (disqualified) {
      applyHint(kind);
      return;
    }
    setPendingHint(kind);
  };

  const applyHint = (kind) => {
    setPendingHint(null);
    if (kind === "letter") revealLetter();
    else if (kind === "word") revealWord();
  };

  const revealLetter = () => {
    const k = key(cursor.row, cursor.col);
    if (!isOpen(puzzle, cursor.row, cursor.col)) return;
    setLetter(cursor.row, cursor.col, solutionAt(puzzle, cursor.row, cursor.col));
    setRevealed((prev) => Object.assign({}, prev, { [k]: true }));
    setHintsTotal((n) => n + 1);
    setTimeout(() => advance(false), 0);
  };

  const revealWord = () => {
    if (!currentEntry) return;
    const cells = entryCells(currentEntry);
    const nextLetters = Object.assign({}, letters);
    const nextRevealed = Object.assign({}, revealed);
    cells.forEach((cell) => {
      const k = key(cell.row, cell.col);
      if (nextLetters[k] !== solutionAt(puzzle, cell.row, cell.col)) {
        nextRevealed[k] = true;
      }
      nextLetters[k] = solutionAt(puzzle, cell.row, cell.col);
    });
    setLetters(nextLetters);
    setRevealed(nextRevealed);
    setHintsTotal(
      (n) => n + Math.max(1, Object.keys(nextRevealed).length - Object.keys(revealed).length)
    );
    setWrong({});
  };

  const clearAll = () => {
    setLetters({});
    setRevealed({});
    setWrong({});
    setComplete(false);
    reportedRef.current = false;
    setStartedAt(Date.now());
    // hintsTotal is deliberately kept: a hint spends the day's eligibility,
    // and wiping the grid must not buy it back.
  };

  /* ------------------------------------------------------------------ render */

  const cols = puzzle.cols || (puzzle.grid[0] ? puzzle.grid[0].length : 0);
  const rows = puzzle.grid.length;
  const highlighted = {};
  if (currentEntry) {
    entryCells(currentEntry).forEach((cell) => {
      highlighted[key(cell.row, cell.col)] = true;
    });
  }

  const progress = totalCells ? Math.round((filledCells / totalCells) * 100) : 0;

  const cellSize =
    "clamp(22px, min(calc((100vw - 72px) / " + cols + "), calc((100vw - 460px) / " + cols + ")), 44px)";

  return (
    <Grid container spacing={4}>
      {/* ------------------------------------------------------------ the grid */}
      <Grid item xs={12} md={7}>
        <Paper
          elevation={0}
          sx={{ p: { xs: 2, md: 3 }, borderRadius: radius.lg, boxShadow: shadow.sm }}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                color={complete ? "success" : "primary"}
                sx={{ height: 8, borderRadius: radius.pill, backgroundColor: tokens.line2 }}
              />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 62, textAlign: "right" }}>
              {filledCells} / {totalCells}
            </Typography>
          </Stack>

          <Box sx={{ overflowX: "auto", pb: 1 }}>
            <Box
              role="grid"
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(" + cols + ", " + cellSize + ")",
                gridAutoRows: cellSize,
                gap: "2px",
                width: "max-content",
                mx: "auto",
                p: "6px",
                borderRadius: radius.md,
                backgroundColor: tokens.ink,
                // Everything inside the grid sizes in `em`, so letters and
                // numbers scale with the responsive cell size.
                fontSize: cellSize,
              }}
            >
              {Array.from({ length: rows }).map((_, r) =>
                Array.from({ length: cols }).map((__, c) => {
                  const k = key(r, c);
                  const open = isOpen(puzzle, r, c);

                  if (!open) {
                    return <Box key={k} sx={{ backgroundColor: "transparent" }} />;
                  }

                  const isCursor = cursor.row === r && cursor.col === c;
                  const inWord = !!highlighted[k];
                  const isWrong = !!wrong[k];
                  const isRevealed = !!revealed[k];

                  let background = tokens.surface;
                  if (complete) background = tokens.green100;
                  else if (isCursor) background = tokens.sand100;
                  else if (inWord) background = tokens.blue50;

                  return (
                    <Box key={k} sx={{ position: "relative" }}>
                      {numbers[k] !== undefined && (
                        <Typography
                          component="span"
                          sx={{
                            position: "absolute",
                            top: "0.04em",
                            left: "0.08em",
                            fontSize: "0.28em",
                            lineHeight: 1,
                            fontWeight: 700,
                            color: tokens.ink400,
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        >
                          {numbers[k]}
                        </Typography>
                      )}
                      <Box
                        component="input"
                        ref={(el) => {
                          inputs.current[k] = el;
                        }}
                        value={letters[k] || ""}
                        onChange={(e) => handleChange(r, c, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, r, c)}
                        onFocus={() => setCursor({ row: r, col: c })}
                        onClick={() => handleCellClick(r, c)}
                        readOnly={complete}
                        inputMode="text"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="characters"
                        spellCheck="false"
                        aria-label={"Клітинка, рядок " + (r + 1) + ", колонка " + (c + 1)}
                        sx={{
                          // `display: block` matters: an inline-block input
                          // sits on the text baseline, and the cell inherits
                          // the grid's (large) font size, so the strut would
                          // push every row down and overflow the last one.
                          display: "block",
                          width: "100%",
                          height: "100%",
                          border: isWrong
                            ? "2px solid " + tokens.red600
                            : isCursor
                            ? "2px solid " + tokens.blue600
                            : "1px solid " + tokens.line,
                          borderRadius: "3px",
                          backgroundColor: isWrong ? tokens.red100 : background,
                          textAlign: "center",
                          fontFamily: '"Manrope", "Inter", sans-serif',
                          fontWeight: 700,
                          fontSize: "0.62em",
                          textTransform: "uppercase",
                          color: isRevealed ? tokens.blue600 : tokens.ink,
                          caretColor: "transparent",
                          padding: 0,
                          outline: "none",
                          cursor: "pointer",
                          transition: "background-color .12s ease",
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>

          {currentEntry && (
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 1.6,
                borderRadius: radius.md,
                backgroundColor: tokens.blue50,
                borderColor: tokens.blue100,
              }}
            >
              <Typography variant="caption" sx={{ color: tokens.blue700, fontWeight: 700 }}>
                {currentEntry.num}{" "}
                {currentEntry.dir === ACROSS ? "по горизонталі" : "по вертикалі"} ·{" "}
                {currentEntry.answer.length} літер
              </Typography>
              <Typography sx={{ mt: 0.4, fontWeight: 600 }}>{currentEntry.clue}</Typography>
            </Paper>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DoneAllOutlined />}
              onClick={checkAnswers}
              disabled={complete}
            >
              Перевірити
            </Button>
            <Button
              size="small"
              variant="outlined"
              color={disqualified ? "inherit" : "secondary"}
              startIcon={<LightbulbOutlined />}
              onClick={() => requestHint("letter")}
              disabled={complete}
            >
              Літера
            </Button>
            <Button
              size="small"
              variant="outlined"
              color={disqualified ? "inherit" : "secondary"}
              startIcon={<KeyOutlined />}
              onClick={() => requestHint("word")}
              disabled={complete || !currentEntry}
            >
              Слово
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              startIcon={<RestartAltOutlined />}
              onClick={clearAll}
              sx={{ ml: "auto", color: tokens.ink500 }}
            >
              Очистити
            </Button>
          </Stack>

          {disqualified ? (
            <Alert
              severity="warning"
              icon={<WarningAmberOutlined fontSize="inherit" />}
              sx={{ mt: 2, borderRadius: radius.md, py: 0.5 }}
            >
              Ви скористалися підказкою ({hintsUsed}{" "}
              {plural(hintsUsed, "літера", "літери", "літер")}), тож сьогодні
              результат не зараховується. Кросворд можна дограти для себе.
            </Alert>
          ) : (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5 }}>
              «Перевірити» показує помилки й нічого не коштує. «Літера» та
              «Слово» розкривають відповідь — після них результат уже не
              зараховується.
            </Typography>
          )}

          {solvedAlready && !complete && (
            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
              Сьогоднішній кросворд уже розгадали — але спробуйте свої сили.
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* --------------------------------------------------------------- clues */}
      <Grid item xs={12} md={5}>
        <Stack spacing={3}>
          <ClueList
            title="По горизонталі"
            entries={acrossEntries}
            current={currentEntry}
            letters={letters}
            puzzle={puzzle}
            onSelect={goToEntry}
          />
          <ClueList
            title="По вертикалі"
            entries={downEntries}
            current={currentEntry}
            letters={letters}
            puzzle={puzzle}
            onSelect={goToEntry}
          />
        </Stack>
      </Grid>

      {/* ------------------------------------------- hint confirmation */}
      <Dialog open={pendingHint !== null} onClose={() => setPendingHint(null)} maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>
          {pendingHint === "word" ? "Показати слово цілком?" : "Показати літеру?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {pendingHint === "word"
              ? "Усі літери цього слова стануть на місце."
              : "Правильна літера з'явиться у вибраній клітинці."}{" "}
            Після підказки ви зможете спокійно дограти кросворд, але{" "}
            <Box component="strong" sx={{ color: tokens.ink }}>
              не потрапите до сьогоднішніх переможців
            </Box>
            .
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button color="inherit" onClick={() => setPendingHint(null)}>
            Ще спробую
          </Button>
          <Button variant="contained" color="secondary" onClick={() => applyHint(pendingHint)}>
            Показати
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

/* ---------------------------------------------------------------- clue list */

function ClueList({ title, entries, current, letters, puzzle, onSelect }) {
  if (!entries.length) return null;

  const isDone = (entry) =>
    entryCells(entry).every(
      (cell) => letters[key(cell.row, cell.col)] === solutionAt(puzzle, cell.row, cell.col)
    );

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: radius.lg, boxShadow: shadow.sm }}>
      <Typography variant="overline" sx={{ color: tokens.blue600 }}>
        {title}
      </Typography>
      <Stack sx={{ mt: 1 }}>
        {entries.map((entry) => {
          const active = current === entry;
          const done = isDone(entry);
          return (
            <Box
              key={entry.dir + entry.num}
              onClick={() => onSelect(entry)}
              sx={{
                display: "flex",
                gap: 1.2,
                px: 1.2,
                py: 0.9,
                borderRadius: radius.sm,
                cursor: "pointer",
                backgroundColor: active ? tokens.blue50 : "transparent",
                borderLeft: "3px solid " + (active ? tokens.blue600 : "transparent"),
                "&:hover": { backgroundColor: active ? tokens.blue50 : tokens.line2 },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 800, minWidth: 20, color: active ? tokens.blue700 : tokens.ink400 }}
              >
                {entry.num}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: done ? tokens.ink400 : tokens.ink700,
                  textDecoration: done ? "line-through" : "none",
                }}
              >
                {entry.clue}
                <Box component="span" sx={{ color: tokens.ink400, ml: 0.6 }}>
                  ({entry.answer.length})
                </Box>
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
